/**
 * Voice2Evidence™ client-shareable PDF reports.
 *
 * Generates premium, typographic PDFs (Helvetica + Times via @react-pdf/renderer
 * built-in PDF fonts — no remote font fetch required). Brand chrome (text
 * wordmark + rust accent line) renders on every page. A persistent
 * illustrative-engagement footer disclosure carries through every page.
 */
import {
  Document, Page, Text, View, StyleSheet, Svg, Line, pdf,
} from "@react-pdf/renderer";

const RUST = "#C84C21";
const INK  = "#0F1825";
const MUTE = "#5C6470";
const SOFT = "#A09680";
const CREAM = "#FAF7F1";

const styles = StyleSheet.create({
  page: {
    paddingTop: 56,
    paddingBottom: 64,
    paddingHorizontal: 56,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: INK,
    lineHeight: 1.5,
  },

  /* Brand chrome */
  brandRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  brandLeft:  { flexDirection: "column" },
  brandRight: { flexDirection: "column", alignItems: "flex-end" },
  wordmark:   { fontSize: 11, color: RUST, letterSpacing: 3, fontFamily: "Helvetica-Bold" },
  tmMark:     { fontSize: 6.5, color: RUST, fontFamily: "Helvetica-Bold", marginLeft: 1, paddingTop: 1 },
  tagline:    { fontSize: 8, color: SOFT, letterSpacing: 2, marginTop: 4, marginLeft: 16 },
  reportKind: { fontSize: 8, color: MUTE, letterSpacing: 2, textTransform: "uppercase" },
  reportRef:  { fontSize: 8, color: SOFT, marginTop: 2 },
  rule:       { marginTop: 10, marginBottom: 22, height: 1, backgroundColor: RUST, width: 32 },

  /* Cover */
  eyebrow:    { fontSize: 9, color: RUST, letterSpacing: 2.5, textTransform: "uppercase", marginBottom: 8 },
  title:      { fontSize: 24, color: INK, fontFamily: "Times-Roman", lineHeight: 1.15, marginBottom: 10 },
  subtitle:   { fontSize: 11, color: MUTE, lineHeight: 1.55, marginBottom: 22, maxWidth: 460 },

  /* Cover metadata grid */
  metaGrid:   { marginTop: 14, marginBottom: 22, flexDirection: "row", flexWrap: "wrap" },
  metaCell:   { width: "50%", marginBottom: 14, paddingRight: 12 },
  metaLabel:  { fontSize: 7.5, color: SOFT, letterSpacing: 1.8, textTransform: "uppercase", marginBottom: 3 },
  metaValue:  { fontSize: 11, color: INK },

  /* Section */
  section:    { marginTop: 14, marginBottom: 4 },
  sectionEy:  { fontSize: 8, color: RUST, letterSpacing: 2.5, textTransform: "uppercase", marginBottom: 5 },
  sectionTtl: { fontSize: 14, color: INK, fontFamily: "Times-Roman", marginBottom: 10 },
  paragraph:  { fontSize: 10.5, color: INK, lineHeight: 1.55, marginBottom: 10 },
  bodyMute:   { fontSize: 10, color: MUTE, lineHeight: 1.55, marginBottom: 8 },

  /* List items */
  listItem:   { flexDirection: "row", marginBottom: 8, alignItems: "flex-start" },
  bullet:     { width: 14, fontSize: 10, color: RUST },
  bulletNum:  { width: 22, fontSize: 8, color: RUST, letterSpacing: 1.5, paddingTop: 1 },
  itemBody:   { flex: 1, fontSize: 10.5, color: INK, lineHeight: 1.5 },

  /* Cards */
  card:           { border: 1, borderColor: "#E8E1D2", padding: 12, marginBottom: 10 },
  cardHeader:     { flexDirection: "row", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 },
  cardLabel:      { fontSize: 8, color: RUST, letterSpacing: 1.5, textTransform: "uppercase" },
  pill:           { fontSize: 7.5, color: RUST, borderWidth: 1, borderColor: RUST, padding: 3, letterSpacing: 1.2, textTransform: "uppercase" },
  pillMute:       { fontSize: 7.5, color: MUTE, borderWidth: 1, borderColor: SOFT, padding: 3, letterSpacing: 1.2, textTransform: "uppercase" },
  cardBody:       { fontSize: 10.5, color: INK, lineHeight: 1.5 },
  cardMeta:       { marginTop: 8, paddingTop: 8, borderTopWidth: 1, borderTopColor: "#F0EBDC", flexDirection: "row", flexWrap: "wrap" },
  cardMetaItem:   { width: "50%", marginBottom: 6 },

  /* Illustrative disclosure */
  disclosure:     { marginTop: 18, padding: 10, backgroundColor: "#FDF7E6", borderWidth: 1, borderColor: "#E8D08A" },
  disclosureLbl:  { fontSize: 7.5, color: "#9A6B1A", letterSpacing: 1.8, marginBottom: 4 },
  disclosureBody: { fontSize: 9, color: "#5C4520", lineHeight: 1.5 },

  /* Footer */
  footer: {
    position: "absolute",
    bottom: 28,
    left: 56,
    right: 56,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#E8E1D2",
  },
  footerLeft:  { fontSize: 7, color: SOFT, letterSpacing: 1.4 },
  footerRight: { fontSize: 7, color: SOFT, letterSpacing: 1.4 },

  /* Table (used for gap map / function summaries) */
  th: { fontSize: 7.5, color: RUST, letterSpacing: 1.4, textTransform: "uppercase", paddingVertical: 5, paddingHorizontal: 6, backgroundColor: CREAM },
  td: { fontSize: 9.5, color: INK, paddingVertical: 5, paddingHorizontal: 6, borderTopWidth: 1, borderTopColor: "#F0EBDC" },
});

/* Tiny SVG accent line — adds a touch of brand */
function AccentLine() {
  return (
    <Svg width="32" height="2" style={{ marginTop: 10, marginBottom: 22 }}>
      <Line x1="0" y1="1" x2="32" y2="1" stroke={RUST} strokeWidth="2" />
    </Svg>
  );
}

function BrandHeader({ reportKind, reportRef }) {
  return (
    <View fixed>
      <View style={styles.brandRow}>
        <View style={styles.brandLeft}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            {/* Small rust accent mark — graphical brand glyph */}
            <Svg width="10" height="10" style={{ marginRight: 6 }}>
              <Line x1="0" y1="5" x2="10" y2="5" stroke={RUST} strokeWidth="3" />
            </Svg>
            <Text style={styles.wordmark}>VOICE2EVIDENCE</Text>
            <Text style={styles.tmMark}>TM</Text>
          </View>
          <Text style={styles.tagline}>by MarksMan Healthcare</Text>
        </View>
        <View style={styles.brandRight}>
          <Text style={styles.reportKind}>{reportKind}</Text>
          {reportRef ? <Text style={styles.reportRef}>{reportRef}</Text> : null}
        </View>
      </View>
      <AccentLine />
    </View>
  );
}

function PageFooter({ reportKind, eng }) {
  return (
    <View style={styles.footer} fixed>
      <Text style={styles.footerLeft}>
        Voice2Evidence · {reportKind} · {eng?.brand || ""} · {eng?.client_label || ""}
      </Text>
      <Text
        style={styles.footerRight}
        render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
      />
    </View>
  );
}

function IllustrativeNotice() {
  return (
    <View style={styles.disclosure}>
      <Text style={styles.disclosureLbl}>ILLUSTRATIVE ENGAGEMENT</Text>
      <Text style={styles.disclosureBody}>
        This simulated case study is for demonstration only. It is not based on a live engagement, client data, confidential information, or proprietary information from the asset owner. Publicly available references may be used only to demonstrate workflow logic.
      </Text>
    </View>
  );
}

function MetaCell({ label, value }) {
  return (
    <View style={styles.metaCell}>
      <Text style={styles.metaLabel}>{label}</Text>
      <Text style={styles.metaValue}>{value || "—"}</Text>
    </View>
  );
}

function SectionHeader({ eyebrow, title }) {
  return (
    <View style={styles.section}>
      {eyebrow ? <Text style={styles.sectionEy}>{eyebrow}</Text> : null}
      <Text style={styles.sectionTtl}>{title}</Text>
    </View>
  );
}

function Bullet({ n, children }) {
  return (
    <View style={styles.listItem} wrap={false}>
      {n != null ? (
        <Text style={styles.bulletNum}>{String(n).padStart(2, "0")}</Text>
      ) : (
        <Text style={styles.bullet}>•</Text>
      )}
      <Text style={styles.itemBody}>{children}</Text>
    </View>
  );
}

/* ============================================================
 * 1. EXECUTIVE BRIEFING REPORT
 * ============================================================ */
export function ExecutiveBriefingReport({ eng, findings = [], actions = [], gapData }) {
  const topShifts = findings.filter((f) => f.confidence === "Confirmed").slice(0, 5);
  const topGaps = (() => {
    const f = findings.filter((f) => f.confidence === "Gap" || f.confidence === "Emerging").slice(0, 3);
    if (f.length >= 3 || !gapData) return f;
    const tally = {};
    gapData.cells.forEach((c) => { if (c.confidence === "Gap") tally[c.topic] = (tally[c.topic] || 0) + 1; });
    const extra = Object.entries(tally)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3 - f.length)
      .map(([topic]) => ({ theme: topic, confidence: "Gap" }));
    return [...f, ...extra];
  })();
  const implications = findings.filter((f) => f.confidence === "Confirmed").slice(0, 3);
  const ACTION_STATUS_MAP = { "Scoped": "Recommended", "In Progress": "In Progress", "Acted On": "Implemented" };
  const recAct = actions.filter((a) => ACTION_STATUS_MAP[a.status] !== "Implemented").slice(0, 4);
  const monitor = findings.filter((f) => f.confidence === "Emerging").slice(0, 3);

  const today = new Date().toISOString().slice(0, 10);
  const reportKind = "Executive Briefing";
  const reportRef  = `Ref · ${eng?.brand || ""} · ${today}`;

  return (
    <Document title={`Voice2Evidence — ${reportKind} — ${eng?.brand || ""}`}>
      <Page size="A4" style={styles.page}>
        <BrandHeader reportKind={reportKind} reportRef={reportRef} />

        {/* Cover */}
        <Text style={styles.eyebrow}>EXECUTIVE BRIEFING</Text>
        <Text style={styles.title}>What changed. Why it matters. What to do next.</Text>
        <Text style={styles.subtitle}>
          The senior-stakeholder synthesis layer for the {eng?.brand} engagement — interpreted by the methodology team across all five PentaVoice evidence streams and the active decision questions for this cycle.
        </Text>

        <View style={styles.metaGrid}>
          <MetaCell label="Client"            value={eng?.client_label} />
          <MetaCell label="Asset"             value={`${eng?.brand} (${eng?.asset || ""})`} />
          <MetaCell label="Therapeutic area"  value={eng?.ta} />
          <MetaCell label="Indication"        value={eng?.indication} />
          <MetaCell label="Lifecycle phase"   value={eng?.lifecycle_phase} />
          <MetaCell label="Lead methodologist" value={eng?.lead_methodologist} />
          <MetaCell label="Prepared on"       value={today} />
          <MetaCell label="Engagement status" value={eng?.status} />
        </View>

        <IllustrativeNotice />

        <PageFooter reportKind={reportKind} eng={eng} />
      </Page>

      <Page size="A4" style={styles.page}>
        <BrandHeader reportKind={reportKind} reportRef={reportRef} />

        <SectionHeader eyebrow="01" title="Top patient-voice shifts this cycle" />
        {topShifts.length === 0
          ? <Text style={styles.bodyMute}>No shifts surfaced.</Text>
          : topShifts.map((f, i) => <Bullet key={i} n={i + 1}>{f.theme}</Bullet>)
        }

        <SectionHeader eyebrow="02" title="Top evidence gaps to address" />
        {topGaps.length === 0
          ? <Text style={styles.bodyMute}>No gaps surfaced.</Text>
          : topGaps.map((f, i) => <Bullet key={i} n={i + 1}>{f.theme}  ({f.confidence})</Bullet>)
        }

        <SectionHeader eyebrow="03" title="Top strategic implications" />
        {implications.length === 0
          ? <Text style={styles.bodyMute}>No implications surfaced.</Text>
          : implications.map((f, i) => (
              <View key={i} style={styles.card} wrap={false}>
                <View style={styles.cardHeader}>
                  <Text style={styles.cardLabel}>Implication {String(i + 1).padStart(2, "0")}</Text>
                  <Text style={styles.pillMute}>{(f.owners || []).join(" · ")}</Text>
                </View>
                <Text style={styles.cardBody}>{f.evidence_implication}</Text>
              </View>
            ))
        }

        <PageFooter reportKind={reportKind} eng={eng} />
      </Page>

      <Page size="A4" style={styles.page}>
        <BrandHeader reportKind={reportKind} reportRef={reportRef} />

        <SectionHeader eyebrow="04" title="Recommended cross-functional actions" />
        {recAct.length === 0
          ? <Text style={styles.bodyMute}>No actions pending.</Text>
          : recAct.map((a, i) => (
              <View key={i} style={styles.card} wrap={false}>
                <View style={styles.cardHeader}>
                  <Text style={styles.cardLabel}>{a.owner_function} · {a.owner_name}</Text>
                  <Text style={styles.pill}>{ACTION_STATUS_MAP[a.status] || a.status}</Text>
                </View>
                <Text style={styles.cardBody}>{a.recommendation}</Text>
              </View>
            ))
        }

        <SectionHeader eyebrow="05" title="Conditional & Watch recommendations — V2E recommended close-of-loop" />
        {monitor.length === 0
          ? <Text style={styles.bodyMute}>Nothing flagged for monitoring.</Text>
          : monitor.map((f, i) => <Bullet key={i}>{f.theme}</Bullet>)
        }

        <IllustrativeNotice />

        <PageFooter reportKind={reportKind} eng={eng} />
      </Page>
    </Document>
  );
}

/* ============================================================
 * 2. ENGAGEMENT BRIEF REPORT
 * ============================================================ */
const DQ_CATS = ["Access", "Patient Preference", "Medical Narrative", "Lifecycle Strategy", "Evidence Planning"];

export function EngagementBriefReport({ eng }) {
  const today = new Date().toISOString().slice(0, 10);
  const reportKind = "Engagement Brief";
  const reportRef  = `Ref · ${eng?.brand || ""} · ${today}`;

  return (
    <Document title={`Voice2Evidence — ${reportKind} — ${eng?.brand || ""}`}>
      <Page size="A4" style={styles.page}>
        <BrandHeader reportKind={reportKind} reportRef={reportRef} />

        <Text style={styles.eyebrow}>ENGAGEMENT BRIEF</Text>
        <Text style={styles.title}>{eng?.brand} — confirmed scope, asset, and decision questions.</Text>
        <Text style={styles.subtitle}>
          The frame for every signal we capture, every theme we triangulate, and every recommendation we issue. If this is wrong, everything downstream drifts.
        </Text>

        <View style={styles.metaGrid}>
          <MetaCell label="Client"            value={eng?.client_label} />
          <MetaCell label="Asset"             value={`${eng?.brand} (${eng?.asset || ""})`} />
          <MetaCell label="Mechanism"         value={eng?.mechanism} />
          <MetaCell label="Therapeutic area"  value={eng?.ta} />
          <MetaCell label="Indication"        value={eng?.indication} />
          <MetaCell label="Lifecycle phase"   value={eng?.lifecycle_phase} />
          <MetaCell label="Geography"         value="US · EU5 (UK · FR · DE · IT · ES)" />
          <MetaCell label="Primary client teams" value="Medical Affairs · HEOR · Market Access · Patient Engagement" />
          <MetaCell label="Lead methodologist" value={eng?.lead_methodologist} />
          <MetaCell label="Engagement status" value={eng?.status} />
        </View>

        <SectionHeader eyebrow="Engagement objective" title="What this engagement is built to deliver." />
        <Text style={styles.paragraph}>
          {eng?.engagement_objective || `Translate patient voice on ${eng?.indication || "this asset"} into evidence-ready strategy for Medical Affairs, HEOR, Market Access, and Patient Engagement.`}
        </Text>

        <SectionHeader eyebrow="Engagement scope" title="The decisions this cycle supports." />
        <Text style={styles.paragraph}>{eng?.scope}</Text>

        <PageFooter reportKind={reportKind} eng={eng} />
      </Page>

      <Page size="A4" style={styles.page}>
        <BrandHeader reportKind={reportKind} reportRef={reportRef} />

        <SectionHeader eyebrow="Active decision questions" title="What we are actually answering." />
        {(eng?.decision_questions || []).map((q, i) => (
          <View key={i} style={styles.card} wrap={false}>
            <View style={styles.cardHeader}>
              <Text style={styles.cardLabel}>Q{String(i + 1).padStart(2, "0")} · {DQ_CATS[i] || "Strategic"}</Text>
            </View>
            <Text style={styles.cardBody}>{q}</Text>
          </View>
        ))}

        <IllustrativeNotice />

        <PageFooter reportKind={reportKind} eng={eng} />
      </Page>
    </Document>
  );
}

/* ============================================================
 * 3. FUNCTION VIEW REPORT
 * ============================================================ */
export function FunctionViewReport({ eng, functionName, themes = [], implications = [], recommendations = [], deliverables = [], gaps = [] }) {
  const today = new Date().toISOString().slice(0, 10);
  const reportKind = `Function View · ${functionName}`;
  const reportRef  = `Ref · ${eng?.brand || ""} · ${today}`;
  const ACTION_STATUS_MAP = { "Scoped": "Recommended", "In Progress": "In Progress", "Acted On": "Implemented" };

  return (
    <Document title={`Voice2Evidence — ${reportKind} — ${eng?.brand || ""}`}>
      <Page size="A4" style={styles.page}>
        <BrandHeader reportKind={reportKind} reportRef={reportRef} />

        <Text style={styles.eyebrow}>FUNCTION VIEW · {functionName.toUpperCase()}</Text>
        <Text style={styles.title}>The same evidence, reframed for {functionName}.</Text>
        <Text style={styles.subtitle}>
          Themes, implications, and recommended close-of-loop — for the {functionName} team supporting the {eng?.brand} engagement.
        </Text>

        <View style={styles.metaGrid}>
          <MetaCell label="Client"            value={eng?.client_label} />
          <MetaCell label="Asset"             value={`${eng?.brand} (${eng?.asset || ""})`} />
          <MetaCell label="Function"          value={functionName} />
          <MetaCell label="Prepared on"       value={today} />
        </View>

        <SectionHeader eyebrow="01" title="Key patient themes" />
        {themes.length === 0
          ? <Text style={styles.bodyMute}>No confirmed themes mapped to this team yet.</Text>
          : themes.slice(0, 6).map((t, i) => <Bullet key={i}>{t.theme}</Bullet>)
        }

        <SectionHeader eyebrow="02" title="Evidence implications" />
        {implications.length === 0
          ? <Text style={styles.bodyMute}>No implications mapped to this team.</Text>
          : implications.slice(0, 4).map((t, i) => (
              <View key={i} style={styles.card} wrap={false}>
                <Text style={styles.cardBody}>{t.evidence_implication}</Text>
              </View>
            ))
        }

        <PageFooter reportKind={reportKind} eng={eng} />
      </Page>

      <Page size="A4" style={styles.page}>
        <BrandHeader reportKind={reportKind} reportRef={reportRef} />

        <SectionHeader eyebrow="03" title="Recommended actions" />
        {recommendations.length === 0
          ? <Text style={styles.bodyMute}>No recommended actions owned by this team.</Text>
          : recommendations.map((a, i) => (
              <View key={i} style={styles.card} wrap={false}>
                <View style={styles.cardHeader}>
                  <Text style={styles.cardLabel}>{a.owner_name} · Assigned {a.assigned_at}</Text>
                  <Text style={styles.pill}>{ACTION_STATUS_MAP[a.status] || a.status}</Text>
                </View>
                <Text style={styles.cardBody}>{a.recommendation}</Text>
              </View>
            ))
        }

        <SectionHeader eyebrow="04" title="Relevant deliverables" />
        {deliverables.length === 0
          ? <Text style={styles.bodyMute}>No deliverables assigned to this team yet.</Text>
          : deliverables.map((d, i) => (
              <View key={i} style={styles.card} wrap={false}>
                <View style={styles.cardHeader}>
                  <Text style={styles.cardLabel}>{d.function}</Text>
                  <Text style={styles.pill}>{d.status}</Text>
                </View>
                <Text style={styles.cardBody}>{d.title}</Text>
                <Text style={[styles.cardBody, { color: MUTE, fontSize: 9.5, marginTop: 4 }]}>{d.summary}</Text>
              </View>
            ))
        }

        <SectionHeader eyebrow="05" title="Open evidence gaps" />
        {gaps.length === 0
          ? <Text style={styles.bodyMute}>No open gaps surfaced for this team.</Text>
          : gaps.map((g, i) => <Bullet key={i}>{g.theme}  ({g.confidence})</Bullet>)
        }

        <IllustrativeNotice />

        <PageFooter reportKind={reportKind} eng={eng} />
      </Page>
    </Document>
  );
}

/* ============================================================
 * 4. DELIVERABLE REPORT
 * ============================================================ */
export function DeliverableReport({ eng, deliverable }) {
  const today = new Date().toISOString().slice(0, 10);
  const reportKind = `Deliverable · ${deliverable?.function}`;
  const reportRef  = `Ref · ${eng?.brand || ""} · ${today}`;

  return (
    <Document title={`Voice2Evidence — ${deliverable?.title || "Deliverable"}`}>
      <Page size="A4" style={styles.page}>
        <BrandHeader reportKind={reportKind} reportRef={reportRef} />

        <Text style={styles.eyebrow}>{(deliverable?.function || "").toUpperCase()} · DELIVERABLE</Text>
        <Text style={styles.title}>{deliverable?.title}</Text>
        <Text style={styles.subtitle}>{deliverable?.summary}</Text>

        <View style={styles.metaGrid}>
          <MetaCell label="Client"             value={eng?.client_label} />
          <MetaCell label="Asset"              value={`${eng?.brand} (${eng?.asset || ""})`} />
          <MetaCell label="Function owner"     value={deliverable?.function} />
          <MetaCell label="Methodologist"      value={deliverable?.methodologist} />
          <MetaCell label="Status"             value={deliverable?.status} />
          <MetaCell label="Last updated"       value={deliverable?.last_updated} />
          <MetaCell label="Evidence basis"     value={`${(deliverable?.linked_finding_themes || []).length} linked findings`} />
          <MetaCell label="Prepared on"        value={today} />
        </View>

        <SectionHeader eyebrow="Linked findings" title="The evidence basis for this deliverable." />
        {(deliverable?.linked_finding_themes || []).length === 0
          ? <Text style={styles.bodyMute}>No linked findings recorded.</Text>
          : deliverable.linked_finding_themes.map((t, i) => <Bullet key={i} n={i + 1}>{t}</Bullet>)
        }

        <IllustrativeNotice />

        <PageFooter reportKind={reportKind} eng={eng} />
      </Page>
    </Document>
  );
}

/* ============================================================
 * Helper — generate + download
 * ============================================================ */
export async function downloadPdf(documentElement, filename) {
  const blob = await pdf(documentElement).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}
